import React from "react";
import cs from "classnames";

import { DeviceInformation } from "../../../../Workplaces/EditWorkplace/EditDevice/types";
import { DeviceBattery } from "../DeviceBattery";
import { SectionHeader } from "../ui/SectionHeader";
import { Notice } from "../ui/Notice";

type Sensor = DeviceInformation["sensorInfo"][number];

export type SensorsState = "paired" | "disabled" | "notSynced" | "noData";

// There's no automation flag in the device data yet, so without sensors the
// push status decides between "disabled" and "not synced" (variant 12)
export const getSensorsState = (
  sensors: Sensor[] | null,
  pushAvailable: boolean
): SensorsState => {
  if (sensors === null) {
    return "noData";
  }

  if (sensors.length > 0) {
    return "paired";
  }

  return pushAvailable ? "disabled" : "notSynced";
};

const statusTone = (status: string) => {
  const normalized = status.toLowerCase();

  if (normalized === "connected" || normalized === "disconnected") {
    return normalized;
  }

  return "unknown";
};

interface Props {
  sensorsState: SensorsState;
  sensors: Sensor[];
  lastSync: string;
}

export const PairedSensors = ({ sensorsState, sensors, lastSync }: Props) => (
  <section className="DeviceSection PairedSensors">
    {/* Singular "sensor" for every count, as confirmed with the design */}
    <SectionHeader icon="sensor" title={`Paired sensor (${sensors.length})`} />

    {sensorsState === "paired" && (
      <>
        <ul className="PairedSensors__list">
          {sensors.map((sensor, index) => (
            <li
              key={`${sensor["Sensor ID"]}-${index}`}
              className="PairedSensors__sensor"
            >
              <div className="PairedSensors__row">
                <span className="PairedSensors__name">
                  {sensor["Sensor ID"]}
                </span>
                <span
                  className={cs(
                    "PairedSensors__status",
                    `PairedSensors__status--${statusTone(sensor.Status)}`
                  )}
                >
                  {sensor.Status}
                </span>
              </div>
              <div className="PairedSensors__meta">
                {sensor["Sensor MAC"]} · Battery status
                <DeviceBattery
                  level={sensor["Battery level"]}
                  className="PairedSensors__battery"
                />
              </div>
            </li>
          ))}
        </ul>

        <div className="DeviceSection__meta">
          <p>Last sync: {lastSync}</p>
          <p>Sensor information syncs every 8 hours.</p>
        </div>
      </>
    )}

    {sensorsState === "disabled" && (
      <Notice tone="muted">Sensor automation disabled</Notice>
    )}

    {sensorsState === "notSynced" && (
      <Notice tone="danger">Sensor data not synced</Notice>
    )}

    {sensorsState === "noData" && (
      <Notice tone="muted">Unassigned or no data found.</Notice>
    )}
  </section>
);
